import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Copy, ListChecks, Pencil, Plus, Trash2 } from 'lucide-react';
import {
  offerVlansToOnu,
  type AuthorizePreset,
  type Olt,
  type OltVlanListResponse,
  type Page,
  type Topology,
  type VlanAddressing,
  type VlanMode,
} from '@simpleacs/shared';
import { api } from '../lib/api';
import { formatRelative } from '../lib/format';
import { useSession } from '../lib/session';
import { useTasks } from '../lib/tasks';
import {
  Button,
  Card,
  EmptyState,
  ErrorNotice,
  Field,
  Input,
  PageHeader,
  Pill,
  Spinner,
  StatCard,
  TBody,
  THead,
  TRow,
  Table,
} from '../components/ui';

/** What the tab reads; every dialog write refreshes it from the tasks tray. */
const PRESETS_KEY = ['onu-presets'] as const;

interface HardwareModel {
  id: string;
  name: string;
}

interface SpeedProfileOption {
  id: string;
  name: string;
  direction: 'up' | 'down';
}

/** The chassis families this platform drives. */
const VENDORS = [
  { value: 'zte', label: 'ZTE' },
  { value: 'huawei', label: 'Huawei' },
] as const;

function vendorLabel(vendor: string | null | undefined): string {
  return VENDORS.find((v) => v.value === vendor)?.label ?? 'Any OLT';
}

/**
 * The authorise habits, kept where the rest of the operator's values are.
 *
 * A preset is the wizard's answers under a name: the package, the VLANs, routed
 * or bridged, and where the fibre usually runs. Most subscribers get the same
 * handful of choices and authorising happens at a pole with a phone, so the
 * point of this page is that nobody types them again.
 *
 * Nothing here reaches a chassis. A preset is applied when an ONU is
 * authorised, and every value is checked against that OLT at that moment — this
 * page is where the habits are reviewed and the stale ones found, which is what
 * the "authorised from it" column is for.
 */
export function AuthorizePresetsTab() {
  const { can } = useSession();
  const editable = can('olt.manage');

  const [creating, setCreating] = useState<Partial<AuthorizePreset> | null>(null);
  const [editing, setEditing] = useState<AuthorizePreset | null>(null);
  const [deleting, setDeleting] = useState<AuthorizePreset | null>(null);

  const query = useQuery({
    queryKey: PRESETS_KEY,
    queryFn: () => api.get<Page<AuthorizePreset>>('/onu-presets'),
  });
  const items = query.data?.items ?? [];

  const speeds = useQuery({
    queryKey: ['speed-profiles'],
    queryFn: () => api.get<{ items: SpeedProfileOption[] }>('/speed-profiles'),
  });
  const speedName = (id: string | null) =>
    speeds.data?.items.find((p) => p.id === id)?.name ?? (id ? 'a deleted package' : null);

  const topology = useQuery({
    queryKey: ['topology'],
    queryFn: () => api.get<Topology>('/topology'),
  });
  const zoneName = (id: string | null | undefined) =>
    topology.data?.zones.find((z) => z.id === id)?.name ?? (id ? 'a deleted zone' : null);

  const neverUsed = useMemo(() => items.filter((p) => !p.lastUsedAt).length, [items]);

  return (
    <>
      {/* No `PageHeader`: Presets owns the page title, this is a tab body. The
          description and the action still belong to the tab, because each kind
          of preset is a different thing and only one of them is on screen. */}
      <div className="mb-5 flex flex-wrap items-start justify-between gap-3">
        <p className="max-w-2xl text-sm text-muted">The wizard's answers under a name. Applied in one tap when an ONU is authorised — and every field stays yours to change afterwards.</p>
        {editable ? (
          <Button variant="primary" icon={<Plus className="size-4" />} onClick={() => setCreating({})}>
            New preset
          </Button>
        ) : null}
      </div>

      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <StatCard label="Presets" value={items.length} icon={<ListChecks className="size-4" />} />
        <StatCard
          label="ONUs authorised from them"
          value={items.reduce((sum, p) => sum + (p.useCount ?? 0), 0)}
        />
        <StatCard
          label="Never used"
          value={neverUsed}
          hint={neverUsed > 0 ? 'saved but never authorised through' : undefined}
        />
      </div>

      <Card className="mt-5" title="Your presets">
        <p className="border-b border-line px-5 py-2 text-xs text-faint">
          Nothing here is written to a chassis. A VLAN or package a preset names is applied only
          where that OLT actually has it, and a preset built on one vendor keeps its VLANs and
          packages on the other — only the ONU type, which means a different thing on each family,
          is left for you to choose. Edit a preset in place with its Edit button; ONUs already
          authorised from it are untouched. One field does more than fill a form: a preset that
          names TR-069 or OMCI makes authorising configure it on the chassis straight afterwards,
          and the ONU is only recorded as managed that way once the chassis has accepted it.
        </p>

        {query.isLoading ? (
          <div className="flex justify-center p-10">
            <Spinner />
          </div>
        ) : query.error ? (
          <div className="p-5">
            <ErrorNotice error={query.error} />
          </div>
        ) : items.length === 0 ? (
          <EmptyState
            icon={<ListChecks className="size-8" />}
            title="No presets yet"
            description="The usual way to make one is to fill in the authorise wizard correctly once and press “Save these choices as a preset”. You can also build one here."
          />
        ) : (
          <Table>
            <THead>
              <th className="th">Preset</th>
              <th className="th">Built for</th>
              <th className="th">ONU type</th>
              <th className="th">VLANs</th>
              <th className="th">Connection</th>
              <th className="th">Managed by</th>
              <th className="th">Packages</th>
              <th className="th">Zone</th>
              <th className="th">Authorised from it</th>
              <th className="th" />
            </THead>
            <TBody>
              {items.map((p) => (
                <TRow key={p.id}>
                  <td className="td font-medium text-ink">{p.name}</td>
                  <td className="td">
                    {p.vendor ? (
                      <Pill tone="info">{vendorLabel(p.vendor)}</Pill>
                    ) : (
                      <span className="text-xs text-faint">any OLT</span>
                    )}
                  </td>
                  <td className="td font-mono text-xs">
                    {p.onuType ?? <span className="text-faint">—</span>}
                  </td>
                  <td className="td text-xs tabular-nums">
                    {p.dataVlan ?? '—'}
                    {p.managementVlan ? (
                      <span className="text-faint"> · mgmt {p.managementVlan}</span>
                    ) : null}
                  </td>
                  <td className="td text-xs text-muted">
                    {p.vlanMode === 'bridge' ? 'Bridged' : 'Routed'}
                    {/* A bridged preset has no addressing (2026-10-14). */}
                    {p.vlanMode === 'bridge' ? '' : ` · ${p.addressing === 'dhcp' ? 'DHCP' : p.addressing === 'static' ? 'Static' : 'PPPoE'}`}
                  </td>
                  <td className="td text-xs">
                    {p.managementMode === 'tr069' ? (
                      <Pill tone="success">TR-069</Pill>
                    ) : p.managementMode === 'omci' ? (
                      <Pill>OMCI only</Pill>
                    ) : (
                      <span className="text-faint">left alone</span>
                    )}
                  </td>
                  <td className="td text-xs text-muted">
                    {p.upstreamProfileId || p.downstreamProfileId ? (
                      [speedName(p.downstreamProfileId), speedName(p.upstreamProfileId)]
                        .filter(Boolean)
                        .join(' / ')
                    ) : (
                      <span className="text-faint">chassis default</span>
                    )}
                  </td>
                  <td className="td text-xs text-muted">
                    {zoneName(p.zoneId) ?? <span className="text-faint">—</span>}
                  </td>
                  <td className="td text-xs">
                    {p.useCount ? (
                      <span className="text-muted">
                        {p.useCount} ONU{p.useCount === 1 ? '' : 's'}
                        {p.lastUsedAt ? (
                          <span className="text-faint"> · {formatRelative(p.lastUsedAt)}</span>
                        ) : null}
                      </span>
                    ) : (
                      <span className="text-faint">never</span>
                    )}
                  </td>
                  <td className="td">
                    {editable && (
                      <div className="flex justify-end gap-2">
                        <Button
                          size="sm"
                          icon={<Pencil className="size-3.5" />}
                          onClick={() => setEditing(p)}
                          title="Change this preset in place"
                        >
                          Edit
                        </Button>
                        {/* Duplicate keeps the original and opens a fresh copy to
                            save under a new name — for a variant, not a change. */}
                        <Button
                          size="sm"
                          icon={<Copy className="size-3.5" />}
                          onClick={() => setCreating({ ...p, name: '' })}
                          title="Start a new preset from this one"
                        >
                          Duplicate
                        </Button>
                        <Button
                          size="sm"
                          variant="danger"
                          icon={<Trash2 className="size-3.5" />}
                          onClick={() => setDeleting(p)}
                        >
                          Delete
                        </Button>
                      </div>
                    )}
                  </td>
                </TRow>
              ))}
            </TBody>
          </Table>
        )}
      </Card>

      {creating && <PresetDialog from={creating} onClose={() => setCreating(null)} />}
      {editing && (
        <PresetDialog from={editing} editingId={editing.id} onClose={() => setEditing(null)} />
      )}
      {deleting && <DeletePresetDialog preset={deleting} onClose={() => setDeleting(null)} />}
    </>
  );
}

/**
 * Build a preset from scratch, or from another one.
 *
 * The OLT comes first because everything below it depends on the answer: a
 * VLAN belongs to a chassis, and an ONU type means a different thing on each
 * vendor. Choosing "any OLT" is a real answer — it makes a preset that carries
 * only what travels — and it is what an operator with one habit across a mixed
 * fleet wants.
 *
 * Saving closes the dialog at once and the write runs in the tasks tray; the
 * list refreshes when it lands. The one check that must happen before the
 * dialog goes — TR-069 without a management VLAN — stays on the button.
 */
function PresetDialog({
  from,
  editingId,
  onClose,
}: {
  from: Partial<AuthorizePreset>;
  /** When set, saving PATCHes this preset in place rather than creating one. */
  editingId?: string;
  onClose: () => void;
}) {
  const tasks = useTasks();
  const [name, setName] = useState(from.name ?? '');
  const [vendor, setVendor] = useState<string>(from.vendor ?? '');
  const [oltId, setOltId] = useState('');
  const [onuType, setOnuType] = useState(from.onuType ?? '');
  const [upProfile, setUpProfile] = useState(from.upstreamProfileId ?? '');
  const [downProfile, setDownProfile] = useState(from.downstreamProfileId ?? '');
  const [dataVlan, setDataVlan] = useState(from.dataVlan ? String(from.dataVlan) : '');
  const [mgmtVlan, setMgmtVlan] = useState(from.managementVlan ? String(from.managementVlan) : '');
  const [vlanMode, setVlanMode] = useState<VlanMode>(from.vlanMode ?? 'route');
  const [addressing, setAddressing] = useState<VlanAddressing>(from.addressing ?? 'dhcp');
  const [zoneId, setZoneId] = useState(from.zoneId ?? '');
  const [managementMode, setManagementMode] = useState<string>(from.managementMode ?? '');

  const olts = useQuery({ queryKey: ['olts'], queryFn: () => api.get<Page<Olt>>('/olts') });
  const models = useQuery({
    queryKey: ['onu-hardware'],
    queryFn: () => api.get<{ items: HardwareModel[] }>('/onu-hardware'),
  });
  const speeds = useQuery({
    queryKey: ['speed-profiles'],
    queryFn: () => api.get<{ items: SpeedProfileOption[] }>('/speed-profiles'),
  });
  const topology = useQuery({
    queryKey: ['topology'],
    queryFn: () => api.get<Topology>('/topology'),
  });

  // The VLAN lists come from the chosen OLT, because a VLAN a chassis does not
  // carry cannot reach a subscriber however carefully it is typed.
  const vlans = useQuery({
    queryKey: ['olt', oltId, 'vlans'],
    queryFn: () => api.get<OltVlanListResponse>(`/olts/${encodeURIComponent(oltId)}/vlans`),
    enabled: Boolean(oltId),
  });
  const vlanItems = vlans.data?.items ?? [];
  /**
   * The VLAN the preset ALREADY names counts as "already on".
   *
   * A preset saved when VLAN 1 was offered must stay editable after it stops
   * being: opening it to change the speed profile and finding the data VLAN
   * silently unselectable would either lose the VLAN on save or leave the form
   * showing a value its own select cannot hold. Same rule as an ONU's current
   * VLAN, for the same reason.
   */
  const vlanOffer = offerVlansToOnu(vlanItems, {
    vlan1Enabled: vlans.data?.vlan1Selectable === true,
    alreadyOn: dataVlan ? [Number(dataVlan)] : [],
  });
  const subscriberVlans = vlanOffer.offered;
  const managementVlans = vlanItems.filter((v) => v.usedForMgmt);

  const speedItems = speeds.data?.items ?? [];
  const zones = topology.data?.zones ?? [];

  const save = () => {
    // Snapshotted: the dialog is gone by the time the write settles.
    const body = {
      name: name.trim(),
      onuType: onuType.trim() || null,
      upstreamProfileId: upProfile || null,
      downstreamProfileId: downProfile || null,
      vlanMode,
      // A bridged subscriber has no addressing of the ONU's own — DHCP is the neutral placeholder (2026-10-14).
      addressing: vlanMode === 'bridge' ? 'dhcp' : addressing,
      dataVlan: dataVlan ? Number(dataVlan) : null,
      managementVlan: mgmtVlan ? Number(mgmtVlan) : null,
      vendor: vendor || null,
      zoneId: zoneId || null,
      managementMode: managementMode || null,
    };
    onClose();
    tasks.start({
      label: `${editingId ? 'Save' : 'Create'} authorise preset ${body.name}`,
      run: () =>
        editingId
          ? api.patch<AuthorizePreset>(`/onu-presets/${encodeURIComponent(editingId)}`, body)
          : api.post<AuthorizePreset>('/onu-presets', body),
      done: () => `“${body.name}” ${editingId ? 'updated' : 'saved'}.`,
      invalidate: [PRESETS_KEY],
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto p-4 sm:p-8">
      <div className="absolute inset-0 bg-black/60" onClick={onClose} />
      <div className="relative w-full max-w-2xl rounded-xl border border-line bg-surface shadow-2xl">
        <header className="border-b border-line px-5 py-4">
          <h2 className="text-sm font-semibold text-ink">
            {editingId
              ? `Edit ${from.name ?? 'preset'}`
              : from.id
                ? `New preset from ${from.onuType ?? 'an existing one'}`
                : 'New preset'}
          </h2>
          <p className="mt-0.5 text-xs text-muted">
            Saved in SimpleACS. It fills the authorise wizard and writes nothing on its own
            {editingId ? ' — editing changes the habit, not any ONU already authorised.' : '.'}
          </p>
        </header>

        <div className="space-y-4 p-5">
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Name" hint="What this habit is called — “Standard residential”">
              <Input
                value={name}
                autoFocus
                maxLength={60}
                onChange={(e) => setName(e.target.value)}
                placeholder="Standard residential"
              />
            </Field>
            <Field
              label="Built for"
              hint={
                vendor
                  ? 'On another vendor its VLANs and packages still apply; the ONU type does not'
                  : 'Carries only what travels between vendors'
              }
            >
              <select
                className="input"
                value={vendor}
                onChange={(e) => setVendor(e.target.value)}
              >
                <option value="">Any OLT</option>
                {VENDORS.map((v) => (
                  <option key={v.value} value={v.value}>
                    {v.label}
                  </option>
                ))}
              </select>
            </Field>
          </div>

          <Field
            label="VLANs from"
            hint={
              oltId
                ? 'The VLAN lists below are this chassis’s own'
                : 'Pick an OLT to choose VLANs from the ones it carries'
            }
          >
            <select
              className="input"
              value={oltId}
              onChange={(e) => {
                setOltId(e.target.value);
                const olt = olts.data?.items.find((o) => o.id === e.target.value);
                // The vendor follows the chassis the operator is choosing from:
                // being asked twice is how the two come to disagree.
                if (olt?.vendor) setVendor(olt.vendor);
              }}
            >
              <option value="">Type the VLAN numbers by hand</option>
              {(olts.data?.items ?? []).map((o) => (
                <option key={o.id} value={o.id}>
                  {o.name}
                </option>
              ))}
            </select>
          </Field>

          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <Field label="Subscriber VLAN" hint="The VLAN their traffic rides">
              {oltId ? (
                <select
                  className="input"
                  value={dataVlan}
                  onChange={(e) => setDataVlan(e.target.value)}
                >
                  <option value="">Not set</option>
                  {subscriberVlans.map((v) => (
                    <option key={v.id} value={v.vlanId}>
                      {v.vlanId}
                      {v.description ? ` — ${v.description}` : ''}
                    </option>
                  ))}
                </select>
              ) : (
                <Input
                  type="number"
                  min={1}
                  max={4094}
                  value={dataVlan}
                  onChange={(e) => setDataVlan(e.target.value)}
                  placeholder="—"
                />
              )}
            </Field>
            <Field label="Management VLAN" hint="How SimpleACS reaches the ONU">
              {oltId ? (
                <select
                  className="input"
                  value={mgmtVlan}
                  onChange={(e) => setMgmtVlan(e.target.value)}
                >
                  <option value="">Not set</option>
                  {managementVlans.map((v) => (
                    <option key={v.id} value={v.vlanId}>
                      {v.vlanId}
                      {v.description ? ` — ${v.description}` : ''}
                    </option>
                  ))}
                </select>
              ) : (
                <Input
                  type="number"
                  min={1}
                  max={4094}
                  value={mgmtVlan}
                  onChange={(e) => setMgmtVlan(e.target.value)}
                  placeholder="—"
                />
              )}
            </Field>
            <Field
              label="Mode"
              hint={
                vlanMode === 'bridge'
                  ? 'The subscriber’s own router terminates it'
                  : 'The ONU terminates it'
              }
            >
              <select
                className="input"
                value={vlanMode}
                onChange={(e) => setVlanMode(e.target.value as VlanMode)}
              >
                <option value="route">Routing</option>
                <option value="bridge">Bridging</option>
              </select>
            </Field>
            {vlanMode === 'route' && (
              <Field label="Addressing" hint="Filled in per subscriber afterwards">
                <select
                  className="input"
                  value={addressing}
                  onChange={(e) => setAddressing(e.target.value as VlanAddressing)}
                >
                  <option value="dhcp">DHCP</option>
                  <option value="static">Static</option>
                  <option value="pppoe">PPPoE</option>
                </select>
              </Field>
            )}
          </div>

          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <Field label="ONU type" hint="The model this habit is for">
              <select className="input" value={onuType} onChange={(e) => setOnuType(e.target.value)}>
                <option value="">Not set — decided per ONU</option>
                {(models.data?.items ?? [])
                  .slice()
                  .sort((a, b) => a.name.localeCompare(b.name))
                  .map((m) => (
                    <option key={m.id} value={m.name}>
                      {m.name}
                    </option>
                  ))}
              </select>
            </Field>
            {(['down', 'up'] as const).map((direction) => {
              const value = direction === 'up' ? upProfile : downProfile;
              const set = direction === 'up' ? setUpProfile : setDownProfile;
              return (
                <Field
                  key={direction}
                  label={direction === 'up' ? 'Upload speed' : 'Download speed'}
                  hint="A package you sell"
                >
                  <select className="input" value={value} onChange={(e) => set(e.target.value)}>
                    <option value="">The chassis default</option>
                    {speedItems
                      .filter((p) => p.direction === direction)
                      .map((p) => (
                        <option key={p.id} value={p.id}>
                          {p.name}
                        </option>
                      ))}
                  </select>
                </Field>
              );
            })}
            <Field label="Zone" hint="Where this habit's fibre usually runs">
              <select className="input" value={zoneId} onChange={(e) => setZoneId(e.target.value)}>
                <option value="">Not set</option>
                {zones.map((z) => (
                  <option key={z.id} value={z.id}>
                    {z.name}
                  </option>
                ))}
              </select>
            </Field>
          </div>

          {/* The one field that makes authorising do a second write. Said
              plainly, because it is the difference between filling a form and
              configuring a chassis. */}
          <Field
            label="Managed by"
            hint={
              managementMode === 'tr069'
                ? 'Authorising will also point the ONU at your ACS — it needs the management VLAN above'
                : managementMode === 'omci'
                  ? 'Authorising will also take the ONU off TR-069'
                  : 'This preset does not dictate the mode — authorising uses the platform default, TR-069'
            }
            error={
              managementMode === 'tr069' && !mgmtVlan
                ? 'Pick a management VLAN first — TR-069 without one is accepted by the chassis and the ONU never informs.'
                : undefined
            }
          >
            <select
              className="input"
              value={managementMode}
              onChange={(e) => setManagementMode(e.target.value)}
            >
              <option value="">Platform default (TR-069)</option>
              <option value="tr069">TR-069</option>
              <option value="omci">OMCI only</option>
            </select>
          </Field>
        </div>

        <footer className="flex items-center justify-end gap-2 border-t border-line px-5 py-4">
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            disabled={!name.trim() || (managementMode === 'tr069' && !mgmtVlan)}
            onClick={save}
          >
            {editingId ? 'Save' : 'Create'}
          </Button>
        </footer>
      </div>
    </div>
  );
}

/** Removing a habit. ONUs authorised through it are untouched — a preset only
 *  ever filled in a form; what reached the chassis belongs to the ONU now. */
function DeletePresetDialog({
  preset,
  onClose,
}: {
  preset: AuthorizePreset;
  onClose: () => void;
}) {
  const tasks = useTasks();
  const call = () => {
    onClose();
    tasks.start({
      label: `Delete authorise preset ${preset.name}`,
      run: () => api.delete(`/onu-presets/${encodeURIComponent(preset.id)}`),
      done: () => `“${preset.name}” deleted.`,
      invalidate: [PRESETS_KEY],
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto p-4 sm:p-8">
      <div className="absolute inset-0 bg-black/60" onClick={onClose} />
      <div className="relative w-full max-w-lg rounded-xl border border-line bg-surface shadow-2xl">
        <header className="border-b border-line px-5 py-4">
          <h2 className="text-sm font-semibold text-ink">Delete {preset.name}</h2>
          <p className="mt-0.5 text-xs text-muted">
            It stops being offered in the authorise wizard. Subscribers authorised through it keep
            everything they were given.
          </p>
        </header>
        <div className="space-y-3 p-5">
          {(preset.useCount ?? 0) > 0 && (
            <div className="rounded-lg border border-warn/30 bg-warn-soft px-3 py-2 text-sm text-warn">
              {preset.useCount} ONU{preset.useCount === 1 ? ' was' : 's were'} authorised from this
              preset. Whoever is doing that work will have to fill the wizard in by hand.
            </div>
          )}
        </div>
        <footer className="flex items-center justify-end gap-2 border-t border-line px-5 py-4">
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="danger" onClick={call}>
            Delete
          </Button>
        </footer>
      </div>
    </div>
  );
}
